import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { PushDeviceDto, PushStatusDto } from '@pickem/shared';
import { api } from '../api/client';
import { isIos, isStandalone } from './usePwa';

/**
 * Web Push, from the browser's side.
 *
 * Enabling notifications is a three-party handshake and every party can refuse:
 *
 *   1. The browser asks the user for permission — a decision that, once denied, this code
 *      cannot ask about again. Only the user can undo it, in site settings.
 *   2. The browser registers with its push service (FCM, Mozilla, Apple) and gets back an
 *      endpoint plus two keys.
 *   3. Those go to our server, which uses them to deliver.
 *
 * Each step is reported separately, because "notifications aren't working" has three
 * completely different fixes depending on which one failed.
 */

export type PushSupport =
  /** This browser has no Push API at all. */
  | 'unsupported'
  /** iOS Safari in a tab: push exists, but only for an installed home-screen app. */
  | 'needs-install'
  | 'supported';

export interface PushState {
  support: PushSupport;
  /** False when the server has no VAPID keys, so subscribing would achieve nothing. */
  configured: boolean;
  permission: NotificationPermission;
  /** True when this device is registered with the server. */
  subscribed: boolean;
  /** Devices registered to this account, including ones that aren't this device. */
  deviceCount: number;
  /**
   * The devices themselves, once they have been asked for.
   *
   * Loaded on demand rather than with the status, because only the settings screen needs
   * them and the home badge — which also uses this hook — does not.
   */
  devices: PushDeviceDto[];
  devicesLoaded: boolean;
  loadDevices: () => void;
  removingDeviceId: string | null;
  removeDevice: (deviceId: string) => void;

  isBusy: boolean;
  error: string | null;
  notice: string | null;
  reminderHours: number;
  finalCallHours: number;
  enable: () => void;
  disable: () => void;
  sendTest: () => void;
}

/**
 * The VAPID key travels as base64url, but `applicationServerKey` wants raw bytes.
 * Padding is re-added because base64url drops it and `atob` will not.
 */
function decodeVapidKey(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url.padEnd(base64Url.length + ((4 - (base64Url.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  // Allocated explicitly rather than via `Uint8Array.from`, which infers the looser
  // `ArrayBufferLike` and will not satisfy `applicationServerKey`'s BufferSource.
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * The service worker registration, or null if there isn't one.
 *
 * `serviceWorker.ready` resolves when a worker becomes active — and if none is ever
 * registered it simply never settles. Not a rejection, not a timeout: it hangs, and so
 * does every `await` behind it.
 *
 * That is not hypothetical. It is the normal state in development, where the PWA plugin
 * registers no worker at all, and it is reachable in production any time registration
 * fails. Awaiting it bare gives a screen that renders nothing forever with no error to
 * show for it, which is exactly how the device list first came out blank.
 *
 * So it is raced against a deadline. Losing that race is a real answer — no worker means
 * no subscription — and one the caller can act on.
 */
async function readyRegistration(timeoutMs = 3000): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  try {
    return await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), timeoutMs)),
    ]);
  } catch {
    return null;
  }
}

function detectSupport(): PushSupport {
  if (typeof window === 'undefined') return 'unsupported';
  const hasApi = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (!hasApi) {
    // Safari on an iPhone only exposes the Push API to an installed web app, so an absent
    // API there means "install it first" rather than "your browser cannot do this".
    return isIos() && !isStandalone() ? 'needs-install' : 'unsupported';
  }
  if (isIos() && !isStandalone()) return 'needs-install';
  return 'supported';
}

export function usePush(enabled: boolean): PushState {
  const qc = useQueryClient();
  const [support] = useState<PushSupport>(detectSupport);
  const [status, setStatus] = useState<PushStatusDto | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    typeof Notification === 'undefined' ? 'default' : Notification.permission,
  );
  const [isBusy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [devices, setDevices] = useState<PushDeviceDto[]>([]);
  const [devicesLoaded, setDevicesLoaded] = useState(false);
  const [removingDeviceId, setRemovingDeviceId] = useState<string | null>(null);

  // What the server knows, plus whether *this particular device* already has a live
  // subscription. The two can disagree — a second phone, or one whose data was cleared —
  // so the button has to reflect the device in front of you, not the account.
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    void (async () => {
      try {
        const serverStatus = await api.get<PushStatusDto>('/push/status');
        if (!cancelled) setStatus(serverStatus);
      } catch {
        // A failure here only means the toggle renders unavailable, which is honest.
      }

      if (support !== 'supported') return;
      try {
        const registration = await readyRegistration();
        const existing = (await registration?.pushManager.getSubscription()) ?? null;
        if (!cancelled) setSubscribed(existing !== null);
      } catch {
        if (!cancelled) setSubscribed(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, support]);

  const enable = useCallback(() => {
    setError(null);
    setNotice(null);
    setBusy(true);

    void (async () => {
      try {
        const publicKey = status?.publicKey;
        if (!publicKey) throw new Error('This server is not set up to send notifications yet.');

        const result = await Notification.requestPermission();
        setPermission(result);
        if (result !== 'granted') {
          throw new Error(
            result === 'denied'
              ? 'Notifications are blocked for this site. You will need to allow them in your browser settings — the app cannot ask again.'
              : 'Notification permission was dismissed.',
          );
        }

        const registration = await readyRegistration();
        if (!registration) {
          throw new Error(
            'The app is still starting up in the background. Reload the page and try again.',
          );
        }
        // Reuse rather than re-subscribe: a browser refuses a second subscription with a
        // different key, and the existing one is still perfectly good.
        const subscription =
          (await registration.pushManager.getSubscription()) ??
          (await registration.pushManager.subscribe({
            // Required, and a promise we keep: the service worker always shows something.
            userVisibleOnly: true,
            applicationServerKey: decodeVapidKey(publicKey),
          }));

        const json = subscription.toJSON() as {
          endpoint?: string;
          keys?: { p256dh?: string; auth?: string };
        };
        if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) {
          throw new Error('The browser returned an incomplete subscription.');
        }

        const next = await api.post<{ deviceCount: number }>('/push/subscribe', {
          endpoint: json.endpoint,
          keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
        });

        setSubscribed(true);
        setStatus((prev) => (prev ? { ...prev, deviceCount: next.deviceCount } : prev));
        setNotice('Notifications are on for this device.');
        void qc.invalidateQueries({ queryKey: ['push'] });
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : 'Could not turn on notifications.');
      } finally {
        setBusy(false);
      }
    })();
  }, [qc, status?.publicKey]);

  const disable = useCallback(() => {
    setError(null);
    setNotice(null);
    setBusy(true);

    void (async () => {
      try {
        const registration = await readyRegistration();
        const subscription = await registration?.pushManager.getSubscription();

        if (subscription) {
          // Tell the server first. If the browser-side unsubscribe succeeded and the
          // server call then failed, we would be left holding an endpoint that is already
          // dead — deliverable in theory, silently discarded in practice.
          await api
            .post('/push/unsubscribe', { endpoint: subscription.endpoint })
            .catch(() => undefined);
          await subscription.unsubscribe();
        }

        setSubscribed(false);
        setStatus((prev) =>
          prev ? { ...prev, deviceCount: Math.max(0, prev.deviceCount - 1) } : prev,
        );
        setNotice('Notifications are off for this device.');
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : 'Could not turn off notifications.');
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  /**
   * This browser's own push endpoint, or null when it has no subscription.
   *
   * Used to mark which row in the device list is the phone in your hand, and to know
   * whether removing a row also has to tear down the local registration.
   */
  const localEndpoint = useCallback(async (): Promise<string | null> => {
    if (support !== 'supported') return null;
    try {
      const registration = await readyRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      return subscription?.endpoint ?? null;
    } catch {
      return null;
    }
  }, [support]);

  const loadDevices = useCallback(() => {
    void (async () => {
      try {
        const endpoint = await localEndpoint();
        // POST for a read: an endpoint is a delivery capability, and query strings end up
        // in history and logs. The server explains the same reasoning at the route.
        const list = await api.post<PushDeviceDto[]>('/push/devices', {
          ...(endpoint ? { endpoint } : {}),
        });
        setDevices(list);
        setDevicesLoaded(true);
      } catch {
        // A failure here leaves the list unrendered, which is honest — it says nothing
        // about whether notifications themselves are working.
        setDevicesLoaded(true);
      }
    })();
  }, [localEndpoint]);

  /**
   * Remove one device from the account.
   *
   * Two halves, and the second is the one that is easy to forget: the server drops the
   * subscription row, but if the row belonged to *this* browser then this browser still
   * holds a live `PushSubscription` that would re-register on the next visit and quietly
   * undo the removal. So the response says which endpoint went, and we tear ours down when
   * it matches.
   */
  const removeDevice = useCallback(
    (deviceId: string) => {
      setError(null);
      setNotice(null);
      setRemovingDeviceId(deviceId);

      void (async () => {
        try {
          const result = await api.delete<{
            removedEndpoint: string;
            devices: PushDeviceDto[];
            deviceCount: number;
          }>(`/push/devices/${deviceId}`);

          setDevices(result.devices);
          setStatus((prev) => (prev ? { ...prev, deviceCount: result.deviceCount } : prev));

          if ((await localEndpoint()) === result.removedEndpoint) {
            const registration = await readyRegistration();
            const subscription = await registration?.pushManager.getSubscription();
            await subscription?.unsubscribe();
            setSubscribed(false);
            setNotice('Notifications are off for this device.');
          } else {
            setNotice('That device will stop receiving notifications.');
          }
        } catch (failure) {
          setError(
            failure instanceof Error ? failure.message : 'Could not remove that device.',
          );
        } finally {
          setRemovingDeviceId(null);
        }
      })();
    },
    [localEndpoint],
  );

  const sendTest = useCallback(() => {
    setError(null);
    setNotice(null);
    setBusy(true);
    void api
      .post<{ delivered: number; note: string }>('/push/test')
      .then(
        (result) => setNotice(result.note),
        (failure: unknown) =>
          setError(failure instanceof Error ? failure.message : 'Could not send a test.'),
      )
      .finally(() => setBusy(false));
  }, []);

  return {
    support,
    configured: status?.configured ?? false,
    permission,
    subscribed,
    deviceCount: status?.deviceCount ?? 0,
    devices,
    devicesLoaded,
    loadDevices,
    removingDeviceId,
    removeDevice,
    isBusy,
    error,
    notice,
    reminderHours: status?.reminderHours ?? 24,
    finalCallHours: status?.finalCallHours ?? 2,
    enable,
    disable,
    sendTest,
  };
}
